<template>
  <div>
    <ts-page-title :title="$t('generalLedger.pageTitle')" :breadcrumb="[
      { text: $t('home'), href: '/' },
      {
        text: $t('generalLedger.pageTitle'),
        active: true
      }
    ]" />
    <ts-panel>
      <ts-panel-wrapper>
        <div class="row">
          <div class="col-md-3">
            <div class="form-group">
              <ts-card>
                <ts-loading-banner :loading="loading">
                  <form>
                    <fieldset>
                      <legend>{{ $t('generalLedger.criteria') }}</legend>
                      <div class="row mt-3">
                        <div class="col-md-12">
                          <div class="form-group">
                            <label class="form-col-label control-label">{{
                              $t('generalLedger.branch')
                            }}</label>
                            <ts-branch-filter @filter="value => (model.branch_id = value)" :isPlaceholder="true"
                              style="width: 100%" />
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-12">
                          <label class="form-col-label control-label required">{{ $t('generalLedger.transactionDate')
                          }}</label>
                          <DatePicker v-model="model.dateRange" type="daterange" placement="bottom-end"
                            :placeholder="$t('selectDateRange')" style="width: 100%" :transfer="true" format="dd-MM-yyyy"
                            @on-change="onChangeTransactionDate"></DatePicker>
                          <div class="text-danger" v-if="errors.has('dateRange')">
                            {{ errors.first('dateRange') }}
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-12">
                          <label class="form-col-label control-label required">{{ $t('generalLedger.currency')
                          }}</label><br />
                          <RadioGroup v-model="model.currency_id" size="small" @on-change="getCoaSetup">
                            <Radio class="mt-1" :label="c.currency_id" border v-for="(c, index) in currencies"
                              :key="index">
                              {{ c.currency_code }}
                            </Radio>
                          </RadioGroup>
                          <div class="text-danger" v-if="errors.has('currency_id')">
                            {{ errors.first('currency_id') }}
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-12">
                          <label class="form-col-label control-label">{{
                            $t('generalLedger.coaCategory')
                          }}</label>
                          <select v-model.number="model.acc_cat_id" class="form-control" @change="getCoaSetup">
                            <option :value="null">
                              {{ $t('select') }}
                            </option>
                            <option v-for="(cat, index) in coaCategories" :key="index" :value="cat.acc_cat_id">
                              {{ cat.acc_cat_name_en }} |
                              {{ cat.acc_cat_type }}
                            </option>
                          </select>
                          <div class="text-danger" v-if="errors.has('acc_cat_id')">
                            {{ errors.first('acc_cat_id') }}
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-12">
                          <label class="form-col-label control-label required">{{ $t('generalLedger.account') }}</label>
                          <Select v-model="model.account_id" :filterable="true" :clearable="true"
                            :remote-method="loadCoaSetup" :loading="coaSetupLoading"
                            :placeholder="$t('generalLedger.search')">
                            <Option v-for="item in coaSetups" :value="item.account_id" :key="item.account_id"
                              :label="item.account_number">
                              {{ item.account_number }} |
                              {{ item.account_name_en }}
                            </Option>
                          </Select>
                          <div class="text-danger" v-if="errors.has('account_id')">
                            {{ errors.first('account_id') }}
                          </div>
                        </div>
                      </div>
                      <div class="row mb-3">
                        <div class="col-md-12">
                          <label class="form-label col-label">{{
                            $t('generalJournalRecord.excelTeplate')
                          }}</label>
                        </div>
                        <div class="col-md-12">
                          <RadioGroup v-model="model.excel_template">
                            <Radio class="mt-1" :label="c.value" border v-for="(c, index) in excelTeplate"
                              :key="index">{{ c.label }}
                            </Radio>
                          </RadioGroup>
                        </div>
                      </div>
                      <div class="mb-3 ts-justify-end ts-flex ts-space-x-2">
                        <ts-button outline color="success" @click.prevent="exportExcel" :waiting="exporting"
                          :disabled="exporting">
                          <i class="far fa-file-excel" v-if="!exporting"></i>
                          {{ $t('exportExcel') }}</ts-button>
                        <ts-button color="danger" outline @click.prevent="preview" :waiting="waiting">
                          <i class="far fa-file-pdf" v-if="!waiting"></i>
                          {{ $t('previewPdf') }}</ts-button>
                      </div>
                    </fieldset>
                  </form>
                </ts-loading-banner>
              </ts-card>
            </div>
          </div>
          <div class="col-lg-9">
            <div id="container">
              <ts-preview-pdf v-model="waiting" :src="src" />
            </div>
          </div>
        </div>
      </ts-panel-wrapper>
    </ts-panel>
  </div>
</template>
<script>
import { Errors } from 'form-backend-validation'
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import { trim } from 'lodash'
import * as FileDownload from 'downloadjs'

export default {
  data() {
    return {
      loading: false,
      waiting: false,
      exporting: false,
      coaSetupLoading: false,
      coaSetups: [],
      errors: new Errors(),
      src: '',
      excelTeplate: [
        {
          value: 'standard',
          label: 'Standard'
        },
        {
          value: 'raw-data',
          label: 'Raw Data'
        }
      ],
      model: {
        excel_template: 'standard',
        branch_id: [],
        currency_id: null,
        acc_cat_id: null,
        account_id: null,
        search: null,
        dateRange: [
          moment().format('DD-MM-YYYY'),
          moment().format('DD-MM-YYYY')
        ]
      }
    }
  },
  computed: {
    ...mapState('report/resource', ['currencies', 'coaCategories'])
  },
  created() {
    this.model.currency_id = this.$store.state.authUser.sys_setting.currency_id_from;
  },
  methods: {
    ...mapActions('report/resource', [
      'getCurrency',
      'getCoaCategory',
      'getCoaSetup'
    ]),
    loadCoaSetup(query) {
      this.model.search = query
      setTimeout(() => {
        this.$store
          .dispatch('report/resource/getCoaSetup', this.model)
          .then(response => {
            this.coaSetups = response.data
          })
      }, 700)
    },
    onChangeTransactionDate(dateRange) {
      this.model.dateRange = dateRange[0] && dateRange[1] ? dateRange : []
    },
    getCoaSetup() {
      this.model.account_id = this.model.search = null
      this.coaSetupLoading = true
      this.$store
        .dispatch('report/resource/getCoaSetup', this.model)
        .then(response => {
          this.coaSetups = response.data
        })
        .catch(error => {
          this.notice({ type: 'error', text: error.message })
        })
        .finally(() => {
          this.coaSetupLoading = false
        })
    },
    preview() {
      this.errors = new Errors()
      this.waiting = true
      this.src = ''
      this.$store
        .dispatch('report/accounting/generalLedger', this.model)
        .then(response => {
          this.src = response.url
        })
        .catch(error => {
          this.notice({ type: 'error', text: error.message })
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    exportExcel() {
      this.errors = new Errors()
      this.exporting = true

      this.$store
        .dispatch('report/accounting/generalLedgerExportExcel', this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.notice({ type: 'error', text: error.message })
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.exporting = false))
    },
    notice(not) {
      this.$Notice[not.type]({
        title: 'GENERAL LEDGER',
        desc: not.text
      })
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.getCurrency()
      vm.getCoaCategory()
      vm.getCoaSetup()
    })
  }
}
</script>
